import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import { getAuth, User } from "firebase/auth";
import {
  getFirestore,
  collection,
  addDoc,
  updateDoc,
  doc,
  getDoc,
  onSnapshot,
  query,
  where,
  serverTimestamp,
  getDocs,
} from "firebase/firestore";
import {
  getStorage,
  ref,
  uploadBytes,
  getDownloadURL,
} from "firebase/storage";
import { AppDispatch } from "./store";

/* ------------------------------------------- */
/*                   TYPES                     */
/* ------------------------------------------- */

export interface PartnerAddress {
  street?: string;
  city?: string;
  country?: string;
  county?: string;
  neighborhood?: string;
  zip?: string;
  latitude?: number | null;
  longitude?: number | null;
}

export interface DeliveryZone {
  type?: "local" | "regional" | "international";
  country: string;
  city: string;
  neighborhood: string;
  region: string;
  zipCode: string;
  latitude: number | null;
  longitude: number | null;
  deliveryFee: number;
  deliveryTime: number;
  fulfilledBy: {
    isFBA: boolean;
    isFBS: boolean;
  };
}

// ── Onboarding type is now always stored/read as booleans ──
// (previously a raw number id — 1 = Business, 2 = Individual — with
// nothing normalizing it, so writes and reads could drift out of sync)
export interface OnboardingTypeFlags {
  isBusiness: boolean;
  isIndividual: boolean;
}

export interface CommercePartner {
  brandId: string;   // == Firestore doc ID
  userId: string;    // owner

  name?: string;
  categoryId?: number | null;
  subcategoryId?: number | null;
  businessType?: number | null;
  category?: string | null;

  address?: {
    country?: string;
    street?: string;
    neighborhood?: string;
    city?: string;
    county?: string;
    latitude?: number | null;
    longitude?: number | null;
  };

  onboardingType?: OnboardingTypeFlags | null;
  phone?: string | null;
  image?: string | null;

  joinDate: string;

  isPublished: boolean;
  isFlagged: boolean;
  isComplete: boolean;
  inReview: boolean;
  isDraft: boolean;

  deleted?: boolean;

  [key: string]: any;
}

interface CommercePartnersState {
  partners: CommercePartner[];      // public / browse list — anyone's
  myPartners: CommercePartner[];    // only the signed-in owner's
  myPartnersLoaded: boolean;
  currentPartner: CommercePartner | null;
  loading: boolean;
  error: string | null;
}

/* `partners` is written by fetchAllCommercePartners and the
   business-type listener, both of which return every brand on the
   platform. Owner-scoped screens must never read it — they read
   myPartners, which only listenToUserCommercePartners writes. */
const initialState: CommercePartnersState = {
  partners: [],
  myPartners: [],
  myPartnersLoaded: false,
  currentPartner: null,
  loading: false,
  error: null,
};

/* ------------------------------------------- */
/*              FIREBASE INIT                  */
/* ------------------------------------------- */

const db = getFirestore();
const auth = getAuth();
const storage = getStorage();

/* ------------------------------------------- */
/*          NORMALIZATION HELPERS               */
/* ------------------------------------------- */

// Accepts whatever shape onboardingType arrives in — a legacy numeric id
// (1 = Business, 2 = Individual, matching PartnerCategories.onboardingTypes),
// an already-boolean object, or null/undefined — and always returns a
// consistent { isBusiness, isIndividual } boolean pair (or null).
const toOnboardingTypeFlags = (value: any): OnboardingTypeFlags | null => {
  if (value === undefined || value === null) return null;

  if (typeof value === "object") {
    return {
      isBusiness: !!value.isBusiness,
      isIndividual: !!value.isIndividual,
    };
  }

  const num = Number(value);
  if (Number.isNaN(num)) return null;

  return {
    isBusiness: num === 1,
    isIndividual: num === 2,
  };
};

// Always returns a complete, well-typed address object (never drops
// county/neighborhood, always coerces lat/lng to numbers or null).
const normalizeAddress = (a: any): CommercePartner["address"] => ({
  street: a?.street ?? "",
  city: a?.city ?? "",
  country: a?.country ?? "",
  county: a?.county ?? "",
  neighborhood: a?.neighborhood ?? "",
  latitude:
    typeof a?.latitude === "string"
      ? Number(a.latitude)
      : a?.latitude ?? null,
  longitude:
    typeof a?.longitude === "string"
      ? Number(a.longitude)
      : a?.longitude ?? null,
});

/* ------------------------------------------- */
/*                 PARSER                      */
/* ------------------------------------------- */

const parsePartner = (
  id: string,
  raw: Record<string, any>
): CommercePartner => {
  return {
    brandId: id,
    userId: raw.userId ?? "",

    name: raw.name ?? "",
    categoryId: raw.categoryId ?? null,
    subcategoryId: raw.subcategoryId ?? null,
    businessType: raw.businessType ?? null,
    category: raw.category ?? "",

    // FIX: previously dropped county/neighborhood on read-back, which made
    // a successful Firestore save look like it hadn't persisted.
    address: normalizeAddress(raw.address),

    // FIX: normalized to booleans instead of a raw number.
    onboardingType: toOnboardingTypeFlags(raw.onboardingType),

    phone: raw.phone ?? "",
    image: raw.image ?? "",

    joinDate: raw.joinDate ?? new Date().toISOString(),

    isPublished: raw.isPublished ?? false,
    isFlagged: raw.isFlagged ?? false,
    isComplete: raw.isComplete ?? false,
    inReview: raw.inReview ?? false,
    isDraft: raw.isDraft ?? true,

    deleted: raw.deleted ?? false,
    delivery: Array.isArray(raw.delivery)
      ? raw.delivery.map((z: any) => ({
          type: z.type ?? "local",
          country: z.country ?? "",
          city: z.city ?? "",
          neighborhood: z.neighborhood ?? "",
          region: z.region ?? "",
          zipCode: z.zipCode ?? "",
          latitude: z.latitude ?? null,
          longitude: z.longitude ?? null,
          deliveryFee: z.deliveryFee ?? 0,
          deliveryTime: z.deliveryTime ?? 1,
          fulfilledBy: {
            isFBA: z.fulfilledBy?.isFBA ?? false,
            isFBS: z.fulfilledBy?.isFBS ?? true,
          },
        }))
      : [],

    fulfilledBy: {
      isFBA: raw.fulfilledBy?.isFBA ?? false,
      isFBS: raw.fulfilledBy?.isFBS ?? true,
    },
  };
};

const getDistanceKm = (
  lat1: number,
  lon1: number,
  lat2: number,
  lon2: number
) => {
  const R = 6371;
  const dLat = ((lat2 - lat1) * Math.PI) / 180;
  const dLon = ((lon2 - lon1) * Math.PI) / 180;

  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos((lat1 * Math.PI) / 180) *
      Math.cos((lat2 * Math.PI) / 180) *
      Math.sin(dLon / 2) ** 2;

  return 2 * R * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
};

export const nearestDeliveryLocation = createAsyncThunk<
  { brandId: string; deliveryFee: number; matchedZone: any | null },
  {
    brandId: string;
    userLatitude: number;
    userLongitude: number;
  }
>(
  "commercePartners/deliveryLocation",
  async ({ brandId, userLatitude, userLongitude }) => {
    const docRef = doc(db, "commercePartners", brandId);
    const snap = await getDoc(docRef);

    if (!snap.exists()) {
      return { brandId, deliveryFee: 0, matchedZone: null };
    }

    const partner = snap.data();
    const zones = partner.delivery ?? [];

    let matchedZone: any = null;
    let shortestDistance = Infinity;

    // 1️⃣ LOCAL (≤ 20km)
    for (const zone of zones) {
      if (zone.type !== "local") continue;
      if (zone.latitude == null || zone.longitude == null) continue;

      const distance = getDistanceKm(
        userLatitude,
        userLongitude,
        zone.latitude,
        zone.longitude
      );

      if (distance <= 50 && distance < shortestDistance) {
        shortestDistance = distance;
        matchedZone = zone;
      }
    }

    // 2️⃣ REGIONAL (same country as seller)
    if (!matchedZone) {
      for (const zone of zones) {
        if (zone.type !== "regional") continue;

        if (
          zone.country &&
          partner.address?.country &&
          zone.country === partner.address.country
        ) {
          matchedZone = zone;
          break;
        }
      }
    }

    // 3️⃣ INTERNATIONAL
    if (!matchedZone) {
      matchedZone = zones.find((z: any) => z.type === "international");
    }

    return {
      brandId,
      deliveryFee: matchedZone?.deliveryFee ?? 0,
      matchedZone: matchedZone ?? null,
    };
  }
);

/* ------------------------------------------- */
/*            UPSERT COMMERCE PARTNER           */
/* ------------------------------------------- */

export const upsertCommercePartner = createAsyncThunk<
  CommercePartner,
  { brandId?: string; data: Partial<CommercePartner> }
>("commercePartners/upsert", async ({ brandId, data }) => {
  const user = auth.currentUser as User | null;
  if (!user) throw new Error("User not authenticated");

  const colRef = collection(db, "commercePartners");

  const clean = (obj: any) => {
    const out: any = {};
    Object.keys(obj).forEach((k) => {
      if (obj[k] !== undefined) out[k] = obj[k];
    });
    return out;
  };

  const normalized = clean({
    ...data,
    // FIX: address is now always written as a complete object (street,
    // city, country, county, neighborhood, latitude, longitude) instead
    // of whatever partial subset happened to be passed in. Firestore's
    // updateDoc replaces a top-level map field wholesale, so a partial
    // address object here would have silently wiped previously-saved
    // fields like county/neighborhood.
    address: data.address ? clean(normalizeAddress(data.address)) : undefined,

    // FIX: onboardingType is always normalized to booleans before it's
    // written, regardless of whether the caller passed a legacy numeric
    // id or an object.
    onboardingType:
      data.onboardingType !== undefined
        ? toOnboardingTypeFlags(data.onboardingType)
        : undefined,
  });

  const updateBase = {
    ...normalized,
    updatedAt: serverTimestamp(),
  };

  /* ---------- UPDATE ---------- */
  if (brandId) {
    const refDoc = doc(colRef, brandId);

    // Merge address with whatever is already saved instead of blindly
    // overwriting, so a partial address update (e.g. only lat/lng from
    // an autocomplete pick) never wipes previously-saved fields.
    if (updateBase.address) {
      const existingSnap = await getDoc(refDoc);
      const existingAddress = existingSnap.data()?.address ?? {};
      updateBase.address = clean({
        ...normalizeAddress(existingAddress),
        ...updateBase.address,
      });
    }

    await updateDoc(refDoc, updateBase);

    const snap = await getDoc(refDoc);
    return parsePartner(snap.id, snap.data() || {});
  }

  /* ---------- CREATE ---------- */
  const joinDate = new Date().toISOString();

  const createBase = {
    ...updateBase,
    userId: user.uid,
    joinDate,
    isPublished: false,
    isFlagged: false,
    isComplete: false,
    inReview: false,
    isDraft: true,
    createdAt: serverTimestamp(),
  };

  const newRef = await addDoc(colRef, createBase);

  await updateDoc(newRef, { brandId: newRef.id });

  const snap = await getDoc(newRef);
  return parsePartner(newRef.id, snap.data() || {});
});

/* ------------------------------------------- */
/*           UPLOAD PARTNER LOGO                */
/* ------------------------------------------- */

export const uploadCommercePartnerLogo = createAsyncThunk<
  string,
  { brandId: string; file: File }
>("commercePartners/uploadLogo", async ({ brandId, file }, { dispatch }) => {
  const path = `commercePartners/${brandId}/logo/${file.name}`;
  const fileRef = ref(storage, path);

  await uploadBytes(fileRef, file);
  const url = await getDownloadURL(fileRef);

  await dispatch(upsertCommercePartner({ brandId, data: { image: url } }));

  return url;
});

/* ------------------------------------------- */
/*         LISTEN TO USER PARTNERS              */
/* ------------------------------------------- */

/* ------------------------------------------- */
/*         LISTEN TO USER PARTNERS              */
/* ------------------------------------------- */

let userPartnersUnsub: null | (() => void) = null;

export const listenToUserCommercePartners =
  (userId: string) => (dispatch: any) => {
    if (userPartnersUnsub) { userPartnersUnsub(); userPartnersUnsub = null; }
    if (!userId) { dispatch(setMyPartners([])); return () => {}; }

    const q = query(
      collection(db, "commercePartners"),
      where("userId", "==", userId)
    );

    userPartnersUnsub = onSnapshot(
      q,
      (snap) => {
        dispatch(setMyPartners(snap.docs.map((d) => parsePartner(d.id, d.data()))));
      },
      (err) => {
        console.error("🔥 listenToUserCommercePartners:", err);
        dispatch(setMyPartners([]));
      }
    );

    /* returned so callers can detach on unmount — the module-level
       handle alone leaked the previous user's listener across logout */
    return () => {
      if (userPartnersUnsub) { userPartnersUnsub(); userPartnersUnsub = null; }
    };
  };

/* ------------------------------------------- */
/*     LISTEN TO PARTNERS BY BUSINESS TYPE      */
/* ------------------------------------------- */

let brandsByTypeUnsub: null | (() => void) = null;

export const listenToBrandsByBusinessType =
  (categoryId: number) => (dispatch: any) => {
    if (brandsByTypeUnsub) brandsByTypeUnsub();

    const q = query(
      collection(db, "commercePartners"),
      where("categoryId", "==", categoryId)
    );

    brandsByTypeUnsub = onSnapshot(q, (snap) => {
      dispatch(
        setPartners(snap.docs.map((d) => parsePartner(d.id, d.data())))
      );
    });

    return brandsByTypeUnsub;
  };

/* ------------------------------------------- */
/*        FETCH ALL COMMERCE PARTNERS           */
/* ------------------------------------------- */

export const fetchAllCommercePartners = createAsyncThunk<
  CommercePartner[]
>("commercePartners/fetchAll", async () => {
  const snap = await getDocs(collection(db, "commercePartners"));

  return snap.docs.map((docSnap) => parsePartner(docSnap.id, docSnap.data()));
});

// ---------- Listeners ----------
export const listenToCommerceData =
  (brandId: string) => (dispatch: AppDispatch) => {
    const docRef = doc(db, "commercePartners", brandId);

    const unsubscribe = onSnapshot(
      docRef,
      (docSnap) => {
        if (docSnap.exists()) {
          const raw = docSnap.data() || {};

          dispatch(
            commercePartnersSlice.actions.setCurrentPartner({
              ...raw,
              brandId: docSnap.id,
              userId: raw.userId ?? "",

              name: raw.name ?? "",
              categoryId: raw.categoryId ?? null,
              subcategoryId: raw.subcategoryId ?? null,
              businessType: raw.businessType ?? null,
              category: raw.category ?? null,

              // FIX: same complete, consistent shape as parsePartner()
              address: normalizeAddress(raw.address),

              // FIX: normalized to booleans instead of a raw number.
              onboardingType: toOnboardingTypeFlags(raw.onboardingType),

              phone: raw.phone ?? "",
              image: raw.image ?? "",

              joinDate: raw.joinDate ?? new Date().toISOString(),

              isPublished: raw.isPublished ?? false,
              isFlagged: raw.isFlagged ?? false,
              isComplete: raw.isComplete ?? false,
              inReview: raw.inReview ?? false,
              isDraft: raw.isDraft ?? true,
              deleted: raw.deleted ?? false,

              delivery: raw.delivery ?? [],
              fulfilledBy: {
                isFBA: raw.fulfilledBy?.isFBA ?? false,
                isFBS: raw.fulfilledBy?.isFBS ?? true,
              },

              createdAt: raw.createdAt?.toMillis?.() || null,
              updatedAt: raw.updatedAt?.toMillis?.() || null,
            } as CommercePartner)
          );
        } else {
          dispatch(commercePartnersSlice.actions.setCurrentPartner(null));
        }
      },
      (error) => {
        dispatch(commercePartnersSlice.actions.setError?.(error.message));
      }
    );

    return unsubscribe;
  };

/* ------------------------------------------- */
/*                    SLICE                    */
/* ------------------------------------------- */

const commercePartnersSlice = createSlice({
  name: "commercePartners",
  initialState,
  reducers: {
    setPartners: (state, a: PayloadAction<CommercePartner[]>) => {
      state.partners = a.payload;
    },
    setMyPartners: (state, a: PayloadAction<CommercePartner[]>) => {
      state.myPartners = a.payload;
      state.myPartnersLoaded = true;
    },
    setCurrentPartner: (state, a: PayloadAction<CommercePartner | null>) => {
      state.currentPartner = a.payload;
    },
    clearCommercePartners: (state) => {
      state.partners = [];
      state.myPartners = [];
      state.myPartnersLoaded = false;
      state.currentPartner = null;
    },
    setError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload;
    },
  },
  extraReducers: (b) => {
    // ✅ ALL addCase FIRST
    b.addCase(upsertCommercePartner.fulfilled, (state, a) => {
      const i = state.partners.findIndex((p) => p.brandId === a.payload.brandId);
      if (i !== -1) state.partners[i] = a.payload;
      else state.partners.push(a.payload);

      state.currentPartner = a.payload;
      state.loading = false;
    });

    b.addCase(fetchAllCommercePartners.pending, (state) => {
      state.loading = true;
      state.error = null;
    });

    b.addCase(fetchAllCommercePartners.fulfilled, (state, a) => {
      state.partners = a.payload;
      state.loading = false;
    });

    b.addCase(fetchAllCommercePartners.rejected, (state, a: any) => {
      state.loading = false;
      state.error = a.error?.message || "Failed to fetch partners";
    });

    // ✅ THEN addMatcher LAST
    b.addMatcher(
      (a) => a.type.startsWith("commercePartners/") && a.type.endsWith("/pending"),
      (state) => {
        state.loading = true;
        state.error = null;
      }
    );

    b.addMatcher(
      (a) => a.type.startsWith("commercePartners/") && a.type.endsWith("/rejected"),
      (state, a: any) => {
        state.loading = false;
        state.error = a.payload || "Something went wrong";
      }
    );
  },
});

export const { setPartners, setMyPartners, setCurrentPartner, clearCommercePartners } =
  commercePartnersSlice.actions;

export default commercePartnersSlice.reducer;